import { Injectable } from '@angular/core';
import { EMPTY, Observable, ReplaySubject } from 'rxjs';
import { AncestryApiService } from './ancestry-api.service';
import {
  AncestryResult,
  ChromosomePainting,
  EthnicityEstimate,
  EthnicityEstimateResult,
  Haplogroups,
  NeanderthalIntrogression,
  PhysicalTraits as PhysicalTraits,
} from '../ancestry.interface';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Analysis } from 'src/app/analyses/interfaces/analyses.types';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AncestryService {
  private ancestryResults$: ReplaySubject<AncestryResult>;
  private ethnicityEstimate$: ReplaySubject<EthnicityEstimate>;
  private chromosomePainting$: ReplaySubject<ChromosomePainting>;
  private haplogroups$: ReplaySubject<Haplogroups>;
  private neanderthalIntrogression$: ReplaySubject<NeanderthalIntrogression>;
  private physicalTraits$: ReplaySubject<PhysicalTraits[]>;
  private physicalTraitsResult$: ReplaySubject<Analysis>;

  private ethnicityEstimateInitialized: boolean;
  private ancestryResultsInitialized: boolean;
  private chromosomePaintingInitialized: boolean;
  private haplogroupsInitialized: boolean;
  private neanderthalIntrogressionInitialized: boolean;
  private physicalTraitsInitialized: boolean;
  private physicalTraitsResultsInitialized: boolean;

  constructor(private ancestryApiService: AncestryApiService) {
    this.init();
  }

  init() {
    this.ancestryResults$ = new ReplaySubject<AncestryResult>(1);
    this.ethnicityEstimate$ = new ReplaySubject<EthnicityEstimate>(1);
    this.chromosomePainting$ = new ReplaySubject<ChromosomePainting>(1);
    this.haplogroups$ = new ReplaySubject<Haplogroups>(1);
    this.neanderthalIntrogression$ =
      new ReplaySubject<NeanderthalIntrogression>(1);
    this.physicalTraits$ = new ReplaySubject<PhysicalTraits[]>(1);
    this.physicalTraitsResult$ = new ReplaySubject<Analysis>(1);
  }

  cleanup() {
    this.init();

    this.ethnicityEstimateInitialized = false;
    this.ancestryResultsInitialized = false;
    this.chromosomePaintingInitialized = false;
    this.haplogroupsInitialized = false;
    this.neanderthalIntrogressionInitialized = false;
    this.physicalTraitsInitialized = false;
    this.physicalTraitsResultsInitialized = false;
  }

  getAncestryResults(refresh?: boolean): Observable<AncestryResult> {
    if (refresh || !this.ancestryResultsInitialized) {
      this.ancestryResultsInitialized = true;
      this.ancestryApiService
        .fetchAncestryResults()
        .pipe(
          catchError((err) => {
            this.ancestryResultsInitialized = false;
            this.ancestryResults$.error(err);
            return EMPTY;
          })
        )
        .subscribe((results) => {
          this.ancestryResults$.next(results);
        });
    }

    return this.ancestryResults$.pipe(distinctUntilChanged());
  }

  getEthnicityEstimate(
    analysisId?: string,
    refresh?: boolean
  ): Observable<EthnicityEstimate> {
    if (analysisId && (refresh || !this.ethnicityEstimateInitialized)) {
      this.ethnicityEstimateInitialized = true;
      this.ancestryApiService
        .fetchEthnicityEstimate(analysisId)
        .pipe(
          catchError((err) => {
            this.ethnicityEstimateInitialized = false;
            this.ethnicityEstimate$.error(err);
            return EMPTY;
          })
        )
        .subscribe((ethnicityEstimate) => {
          this.ethnicityEstimate$.next(ethnicityEstimate);
        });
    }

    return this.ethnicityEstimate$.pipe(distinctUntilChanged());
  }

  getEthnicityEstimateResultById(
    resultId: string
  ): Observable<EthnicityEstimateResult> {
    return this.getEthnicityEstimate().pipe(
      map(({ results }) => results.find((r) => r.id === resultId))
    );
  }

  getHaplogroups(
    analysisId: string,
    refresh?: boolean
  ): Observable<Haplogroups> {
    if (analysisId && (refresh || !this.haplogroupsInitialized)) {
      this.haplogroupsInitialized = true;
      this.ancestryApiService
        .fetchHaplogroups(analysisId)
        .pipe(
          catchError((err) => {
            this.haplogroupsInitialized = false;
            this.haplogroups$.error(err);
            return EMPTY;
          })
        )
        .subscribe((haplogroups) => {
          this.haplogroups$.next(haplogroups);
        });
    }

    return this.haplogroups$.pipe(distinctUntilChanged());
  }

  getChromosomePainting(
    analysisId?: string,
    refresh?: boolean
  ): Observable<ChromosomePainting> {
    if (analysisId && (refresh || !this.chromosomePaintingInitialized)) {
      this.chromosomePaintingInitialized = true;
      this.ancestryApiService
        .fetchChromosomePainting(analysisId)
        .pipe(
          catchError((err) => {
            this.chromosomePaintingInitialized = false;
            this.chromosomePainting$.error(err);
            return EMPTY;
          })
        )
        .subscribe((chromosomePainting) => {
          this.chromosomePainting$.next(chromosomePainting);
        });
    }

    return this.chromosomePainting$.pipe(distinctUntilChanged());
  }

  getNeanderthalIntrogression(
    analysisId?: string,
    refresh?: boolean
  ): Observable<NeanderthalIntrogression> {
    if (analysisId && (refresh || !this.neanderthalIntrogressionInitialized)) {
      this.neanderthalIntrogressionInitialized = true;
      this.ancestryApiService
        .fetchNeanderthalIntrogression(analysisId)
        .pipe(
          catchError((err) => {
            this.neanderthalIntrogressionInitialized = false;
            this.neanderthalIntrogression$.error(err);
            return EMPTY;
          })
        )
        .subscribe((neanderthalIntrogression) => {
          this.neanderthalIntrogression$.next(neanderthalIntrogression);
        });
    }

    return this.neanderthalIntrogression$.pipe(distinctUntilChanged());
  }

  getPhysicalTraitsResults(
    productInventoryItemId?: string,
    refresh?: boolean
  ): Observable<PhysicalTraits[]> {
    if (
      productInventoryItemId &&
      (refresh || !this.physicalTraitsInitialized)
    ) {
      this.physicalTraitsInitialized = true;
      this.ancestryApiService
        .fetchPhysicalTraits(productInventoryItemId)
        .pipe(
          map((traits: PhysicalTraits[]) => sortByName(traits)),
          catchError((err) => {
            this.physicalTraitsInitialized = false;
            this.physicalTraits$.error(err);
            return EMPTY;
          })
        )
        .subscribe((results) => {
          this.physicalTraits$.next(results);
        });
    }

    return this.physicalTraits$.pipe(distinctUntilChanged());
  }

  getPhysicalTraitsAnalysisResult(
    analysisId: string,
    refresh?: boolean
  ): Observable<Analysis> {
    if (analysisId && (refresh || !this.physicalTraitsResultsInitialized)) {
      this.physicalTraitsResultsInitialized = true;
      this.physicalTraitsResult$.next(undefined);
      this.ancestryApiService
        .fetchPhysicalTraitAnalysis(analysisId)
        .pipe(
          catchError((err) => {
            this.physicalTraitsResultsInitialized = false;
            this.physicalTraitsResult$.error(err);
            this.physicalTraitsResult$ = new ReplaySubject<Analysis>(1);
            return EMPTY;
          })
        )
        .subscribe((results) => {
          this.physicalTraitsResult$.next(results);
        });
    }

    return this.physicalTraitsResult$.pipe(distinctUntilChanged());
  }
}

export const sortByName = (traits: PhysicalTraits[]) => {
  return traits.sort((a: PhysicalTraits, b: PhysicalTraits) =>
    a.shortName.toLowerCase().localeCompare(b.shortName.toLowerCase())
  );
};
